import { Component, Event, EventEmitter, h, Host, Listen, Prop } from '@stencil/core';
import { Dictionary } from '@wuic-core/types';

interface IconRequest {
  set: string;
  icons: string[];
}

/**
 * @readme
 * The AsteroidIconSource Component is an Icon library loader/manager.
 *
 * ### Features
 * - load entire sets from the cdn
 * - load specific icons from a set from the cdn
 *
 * ### Avaliable Icons
 *
 * - Refer to the [Icons documentation](https://expert-bassoon-30e75971.pages.github.io/)
 *
 */
@Component({
  tag: 'asteroid-icon-source',
  styleUrl: 'asteroid-icon-source.css',
  shadow: true,
})
export class AsteroidIconSource {
  /**
   * Contains the internal iconSet/icon metadata
   */
  public static icons: Dictionary<Dictionary<string>> = {};
  /**
   * Contains the fully loaded set names
   */
  public static loadedSets: string[] = [];

  public static pendingSets: Dictionary<string[] | boolean> = {};

  /**
   * The asteroidIconSourceIconsetLoaded event fires when svg's inside an iconSet are completely loaded
   * @wuicExample
   * el.addEventListener('asteroidIconSourceIconsetLoaded',(event:CustomEvent<string>)=>{
   *  trace('asteroidIconSourceIconsetLoaded',event);
   * });
   */
  @Event() asteroidIconSourceIconsetLoaded: EventEmitter<{ set: string; icons: string[] }>;

  /** URL for cdn to fetch icons
   * @wuicHideExample
   * @wuicExampleValue https://cdn-web.wefox.com/public/pkg/@wefox/icons/1.4.0
   */
  @Prop() src = '';

  @Listen('loadIconsList', { target: 'window' })
  public async handleLoadIconsList(evt: CustomEvent<IconRequest[]>) {
    const iconRequest = evt.detail;
    const setRequests = iconRequest.filter((req) => req.icons === undefined).map((req) => req.set);
    const iconRequests = iconRequest.filter((req) => req.icons);
    if (iconRequests.length) this._handleIconsSubset(iconRequests);
    if (setRequests.length) this._handleIconsSet(setRequests);
  }

  render() {
    return (
      this.src && (
        <Host>
          <slot />
        </Host>
      )
    );
  }

  private _addIconsToPending(iconRequests: IconRequest[]) {
    iconRequests.forEach((iconRequest) => {
      const { set, icons } = iconRequest;
      AsteroidIconSource.pendingSets[set] = (AsteroidIconSource.pendingSets[set] as string[])?.concat(icons) || icons;
    });
  }

  private _addSetsToPending(setsNotLoaded: string[]) {
    const pendingSets = setsNotLoaded.reduce((acc, currentValue) => {
      acc[currentValue] = true;

      return acc;
    }, {});
    AsteroidIconSource.pendingSets = { ...AsteroidIconSource.pendingSets, ...pendingSets };
  }

  private _getNotLoadedIcons(iconRequests: IconRequest[]): IconRequest[] {
    return iconRequests
      .filter((iconRequest) => AsteroidIconSource.pendingSets?.[iconRequest.set] !== true)
      .map((iconRequest) => ({
        set: iconRequest.set,
        icons: iconRequest.icons.filter(
          (icon) =>
            AsteroidIconSource.icons?.[iconRequest.set]?.[icon] === undefined &&
            (AsteroidIconSource.pendingSets?.[iconRequest.set] as string[])?.includes(icon) !== true
        ),
      }))
      .filter((iconRequest) => iconRequest.icons.length > 0);
  }
  private _getNotLoadedSets(sets: string[]): string[] {
    return sets
      .filter((set) => !AsteroidIconSource.loadedSets.includes(set))
      .filter((set) => !AsteroidIconSource.pendingSets?.[set] === true);
  }

  private _getSVG(set: string, content: string): Dictionary<string> {
    const domparser = new DOMParser();

    return content
      .replace(/[\n]/g, '')
      .match(/<symbol(.+?)\/symbol>/g)
      .map((symbol) => symbol.replace('<symbol ', '<svg ').replace('</symbol>', '</svg>'))
      .map((svg) => {
        const doc = domparser.parseFromString(`<div>${svg}</div>`, 'text/html');
        const el = doc.getElementsByTagName('svg')[0];
        el.setAttribute('width', '100%');
        el.setAttribute('height', '100%');

        return doc.getElementsByTagName('svg')[0].outerHTML;
      })
      .reduce((acc, svg) => {
        const id = svg.match(/<svg.+?id="(.+?)"/)[1].replace(`${set}-`, '');
        acc[id] = svg;

        return acc;
      }, {});
  }

  private _getSuccessResponse(response: PromiseFulfilledResult<Response> | PromiseRejectedResult): boolean {
    return response.status === 'fulfilled' && response.value.status === 200;
  }

  private async _handleIconsSet(sets: string[]) {
    const setsNotLoaded = this._getNotLoadedSets(sets);
    this._addSetsToPending(setsNotLoaded);
    const response = await Promise.allSettled(setsNotLoaded.map((set) => fetch(`${this.src}/sprites/${set}.svg`)));
    const successResponse = response.filter(this._getSuccessResponse);
    const successSpriteNames = successResponse.map(
      (res) => res.status === 'fulfilled' && res.value.url.match(/([a-zA-Z-_]+).svg/)[1]
    );
    const sprites = await Promise.all(
      successResponse.map(
        async (res) =>
          res.status === 'fulfilled' && {
            set: res.value.url.match(/([a-zA-Z-_]+).svg/)[1],
            svg: await res.value.text(),
          }
      )
    );
    this._removeSetsFromPending(setsNotLoaded);
    AsteroidIconSource.loadedSets = AsteroidIconSource.loadedSets.concat(successSpriteNames);
    sprites.forEach((sprite) => {
      this._updateIcons(sprite.set, this._getSVG(sprite.set, sprite.svg));
    });
  }

  private async _handleIconsSubset(iconRequest: IconRequest[]) {
    const iconsNotLoaded = this._getNotLoadedIcons(iconRequest);
    if (iconsNotLoaded.length === 0) return;
    this._addIconsToPending(iconRequest);
    const requests = iconsNotLoaded.flatMap((request) =>
      request.icons.map((value) => fetch(`${this.src}/svg/${request.set}/${value}.svg`))
    );
    const responses = await Promise.allSettled(requests);
    const successResponses = responses
      .filter(this._getSuccessResponse)
      .map((response) => response.status === 'fulfilled' && response.value);
    this._removeIconsFromPending(iconRequest);
    const iconsArray = await Promise.all(
      successResponses.map(async (response) => {
        const iconName = response.url.match(/([a-zA-Z\d-_]+)\.svg/)[1];
        const svg = await response.text();
        const set = response.url.match(/\/svg\/([^/]+)/)[1];

        return { svg, iconName, set };
      })
    );
    const subsets = iconsArray.reduce((acc, currentValue) => {
      acc[currentValue.set] = {
        icons: { ...acc?.[currentValue.svg]?.icons, [currentValue.iconName]: currentValue.svg },
      };

      return acc;
    }, {});
    Object.keys(subsets).forEach((key) => {
      this._updateIcons(key, subsets[key].icons);
    });
  }

  private _removeIconsFromPending(iconRequests: IconRequest[]) {
    iconRequests.forEach((iconRequest) => {
      const { set, icons } = iconRequest;
      AsteroidIconSource.pendingSets[set] = (AsteroidIconSource.pendingSets[set] as string[])?.filter((icon) =>
        icons.includes(icon)
      );
    });
  }

  private _removeSetsFromPending(sets: string[]) {
    AsteroidIconSource.pendingSets = Object.keys(AsteroidIconSource.pendingSets)
      .filter((set) => !sets.includes(set))
      .reduce((acc, currentValue) => {
        acc[currentValue] = true;

        return acc;
      }, {});
  }

  private _updateIcons(iconSet: string, svg: Dictionary<string>) {
    AsteroidIconSource.icons[iconSet] = { ...AsteroidIconSource.icons[iconSet], ...svg };
    const iconNames = Object.keys(svg);
    this.asteroidIconSourceIconsetLoaded.emit({ set: iconSet, icons: iconNames });
  }
}
